/**
 * Advanced Scroll Animations
 * Modern scroll-triggered effects and parallax animations
 * Optimized for performance with IntersectionObserver and requestAnimationFrame
 */

class ScrollAnimations {
  constructor() {
    this.observers = [];
    this.parallaxElements = [];
    this.splitTextElements = [];
    this.scrollProgress = 0;
    this.ticking = false;

    this.init();
  }

  init() {
    // Initialize different animation types
    this.initScrollReveal();
    this.initParallax();
    this.initSplitText();
    this.initScrollProgress();
    this.initSmoothScroll();
    this.init3DCards();
    this.initImageReveals();
    this.initFloatingElements();

    // Reinitialize on dynamic content
    this.observeDynamicContent();
  }

  // ===== SCROLL REVEAL ANIMATIONS =====
  initScrollReveal() {
    const animatedElements = document.querySelectorAll(`
      .animate-fade-in,
      .animate-fade-up,
      .animate-scale-in,
      .animate-rotate-in,
      .animate-stagger-children
    `);

    const observerOptions = {
      threshold: 0.1,
      rootMargin: '50px'
    };

    const scrollObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          // Add a small delay for smoother perception
          setTimeout(() => {
            entry.target.classList.add('in-view');
          }, 50);

          // Optional: unobserve after animation to save resources
          if (!entry.target.hasAttribute('data-repeat')) {
            scrollObserver.unobserve(entry.target);
          }
        } else if (entry.target.hasAttribute('data-repeat')) {
          entry.target.classList.remove('in-view');
        }
      });
    }, observerOptions);

    animatedElements.forEach(el => scrollObserver.observe(el));
    this.observers.push(scrollObserver);
  }

  // ===== PARALLAX SCROLLING =====
  initParallax() {
    this.parallaxElements = document.querySelectorAll('.parallax-element');

    if (this.parallaxElements.length === 0) return;

    // Use RAF for smooth parallax
    window.addEventListener('scroll', () => this.handleParallax(), { passive: true });

    // Initial call
    this.handleParallax();
  }

  handleParallax() {
    if (!this.ticking) {
      requestAnimationFrame(() => {
        this.updateParallax();
        this.ticking = false;
      });
      this.ticking = true;
    }
  }

  updateParallax() {
    const scrolled = window.pageYOffset;
    const viewportHeight = window.innerHeight;

    this.parallaxElements.forEach(element => {
      const rect = element.getBoundingClientRect();
      const speed = parseFloat(element.dataset.parallaxSpeed || 0.5);

      // Only animate elements in viewport
      if (rect.bottom >= 0 && rect.top <= viewportHeight) {
        const yPos = -(scrolled * speed);
        element.style.transform = `translateY(${yPos}px)`;
      }
    });
  }

  // ===== SPLIT TEXT ANIMATIONS =====
  initSplitText() {
    const headings = document.querySelectorAll('[data-split-text]:not([data-split-processed])');

    headings.forEach(heading => {
      // Mark as processed to prevent re-splitting
      heading.setAttribute('data-split-processed', 'true');

      // Split text into spans
      const text = heading.textContent;
      const words = text.split(' ');

      heading.innerHTML = '';

      words.forEach((word, wordIndex) => {
        const wordSpan = document.createElement('span');
        wordSpan.className = 'split-word';
        wordSpan.style.display = 'inline-block';

        // Split each word into characters
        [...word].forEach((char, charIndex) => {
          const charSpan = document.createElement('span');
          charSpan.className = 'split-char';
          charSpan.textContent = char;
          charSpan.style.display = 'inline-block';
          charSpan.style.opacity = '0';
          charSpan.style.transform = 'translateY(40px)';
          charSpan.style.transition = `all 0.6s cubic-bezier(0.16, 1, 0.3, 1) ${(wordIndex * 0.1) + (charIndex * 0.02)}s`;
          wordSpan.appendChild(charSpan);
        });

        heading.appendChild(wordSpan);

        // Add space between words
        if (wordIndex < words.length - 1) {
          heading.appendChild(document.createTextNode(' '));
        }
      });

      // Observe for animation trigger
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.split-char').forEach(char => {
              char.style.opacity = '1';
              char.style.transform = 'translateY(0)';
            });
            observer.unobserve(entry.target);
          }
        });
      }, { threshold: 0.1 });

      observer.observe(heading);
      this.observers.push(observer);
    });
  }

  // ===== SCROLL PROGRESS INDICATOR =====
  initScrollProgress() {
    // Create progress bar if it doesn't exist
    let progressBar = document.getElementById('scroll-progress');
    if (!progressBar) {
      progressBar = document.createElement('div');
      progressBar.id = 'scroll-progress';
      progressBar.className = 'scroll-progress';
      document.body.appendChild(progressBar);
    }

    window.addEventListener('scroll', () => {
      const scrollHeight = document.documentElement.scrollHeight - window.innerHeight;
      const scrolled = window.pageYOffset;
      const progress = scrolled / scrollHeight;

      progressBar.style.transform = `scaleX(${progress})`;
    }, { passive: true });
  }

  // ===== SMOOTH SCROLL (ENHANCED) =====
  initSmoothScroll() {
    // Add smooth scroll class to html
    document.documentElement.classList.add('smooth-scroll');

    // Enhanced anchor scrolling with offset
    document.querySelectorAll('a[href^="#"]').forEach(anchor => {
      anchor.addEventListener('click', (e) => {
        const href = anchor.getAttribute('href');
        if (href === '#') return;

        const target = document.querySelector(href);
        if (target) {
          e.preventDefault();

          const headerOffset = 100;
          const elementPosition = target.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          });
        }
      });
    });
  }

  // ===== 3D CARD TILT EFFECT =====
  init3DCards() {
    const cards = document.querySelectorAll('.card-3d, .tilt-effect');

    cards.forEach(card => {
      card.addEventListener('mousemove', (e) => {
        const rect = card.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;

        const centerX = rect.width / 2;
        const centerY = rect.height / 2;

        const rotateX = ((y - centerY) / centerY) * -10;
        const rotateY = ((x - centerX) / centerX) * 10;

        card.style.setProperty('--tilt-x', `${rotateX}deg`);
        card.style.setProperty('--tilt-y', `${rotateY}deg`);
      });

      card.addEventListener('mouseleave', () => {
        card.style.setProperty('--tilt-x', '0deg');
        card.style.setProperty('--tilt-y', '0deg');
      });
    });
  }

  // ===== IMAGE REVEAL EFFECTS =====
  initImageReveals() {
    const images = document.querySelectorAll('.image-reveal');

    const imageObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('revealed');
          imageObserver.unobserve(entry.target);
        }
      });
    }, { threshold: 0.2 });

    images.forEach(img => imageObserver.observe(img));
    this.observers.push(imageObserver);
  }

  // ===== FLOATING BACKGROUND ELEMENTS =====
  initFloatingElements() {
    // Create floating shapes if enabled
    const floatingContainers = document.querySelectorAll('[data-floating-shapes]:not([data-floating-processed])');

    floatingContainers.forEach(container => {
      // Mark as processed to prevent duplicate shapes
      container.setAttribute('data-floating-processed', 'true');

      const shapeCount = parseInt(container.dataset.floatingShapes) || 3;

      for (let i = 0; i < shapeCount; i++) {
        const shape = document.createElement('div');
        shape.className = 'floating-shape';
        shape.style.position = 'absolute';
        shape.style.pointerEvents = 'none';

        // Random properties
        const size = Math.random() * 100 + 50;
        const startX = Math.random() * 100;
        const startY = Math.random() * 100;
        const duration = Math.random() * 20 + 20;

        shape.style.width = `${size}px`;
        shape.style.height = `${size}px`;
        shape.style.left = `${startX}%`;
        shape.style.top = `${startY}%`;
        shape.style.animationDuration = `${duration}s`;
        shape.style.animationDelay = `${Math.random() * 5}s`;
        shape.style.background = `radial-gradient(circle, rgba(215, 255, 1, 0.1), transparent)`;
        shape.style.borderRadius = '50%';

        container.appendChild(shape);
      }
    });
  }

  // ===== MAGNETIC CURSOR EFFECT =====
  initMagneticCursor() {
    const magneticElements = document.querySelectorAll('[data-magnetic]');

    magneticElements.forEach(element => {
      element.addEventListener('mousemove', (e) => {
        const rect = element.getBoundingClientRect();
        const x = e.clientX - rect.left - rect.width / 2;
        const y = e.clientY - rect.top - rect.height / 2;

        const distance = Math.sqrt(x * x + y * y);
        const maxDistance = Math.max(rect.width, rect.height);

        if (distance < maxDistance) {
          const strength = (1 - distance / maxDistance) * 0.3;
          element.style.transform = `translate(${x * strength}px, ${y * strength}px)`;
        }
      });

      element.addEventListener('mouseleave', () => {
        element.style.transform = '';
      });
    });
  }

  // ===== OBSERVE DYNAMIC CONTENT =====
  observeDynamicContent() {
    // Reinitialize animations when new content is added
    // Use debouncing to prevent infinite loops
    let debounceTimer;
    const contentObserver = new MutationObserver(() => {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        this.initScrollReveal();
        this.init3DCards();
      }, 300);
    });

    contentObserver.observe(document.body, {
      childList: true,
      subtree: false // Changed to false to reduce overhead
    });
  }

  // ===== CLEANUP =====
  destroy() {
    // Clean up observers
    this.observers.forEach(observer => observer.disconnect());

    // Remove event listeners
    window.removeEventListener('scroll', this.handleParallax);
  }
}

// ===== UTILITY FUNCTIONS =====

// Debounce function for performance
function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

// Check if element is in viewport
function isInViewport(element) {
  const rect = element.getBoundingClientRect();
  return (
    rect.top >= 0 &&
    rect.left >= 0 &&
    rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
    rect.right <= (window.innerWidth || document.documentElement.clientWidth)
  );
}

// ===== INITIALIZE ON LOAD =====
document.addEventListener('DOMContentLoaded', () => {
  const scrollAnimations = new ScrollAnimations();

  // Store instance globally for access if needed
  window.scrollAnimations = scrollAnimations;

  console.log('Advanced scroll animations initialized.');
});

// Reinitialize on Shopify section load
if (typeof Shopify !== 'undefined') {
  document.addEventListener('shopify:section:load', () => {
    if (window.scrollAnimations) {
      window.scrollAnimations.init();
    }
  });
}
